import { useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { KeyRound, Lock, Mail, User as UserIcon, ArrowRight } from 'lucide-react';
import { api, showApp, state } from '@/core';
import type { User } from '@/types';
import type { AuthMode } from '@/auth-store';

// Vertex shader source code
const vertexSmokeySource = `
  attribute vec4 a_position;
  void main() {
    gl_Position = a_position;
  }
`;

// Fragment shader source code for the smokey background effect
const fragmentSmokeySource = `
precision mediump float;

uniform vec2 iResolution;
uniform float iTime;
uniform vec2 iMouse;
uniform vec3 u_color;

void mainImage(out vec4 fragColor, in vec2 fragCoord){
    vec2 uv = fragCoord / iResolution;
    vec2 centeredUV = (2.0 * fragCoord - iResolution.xy) / min(iResolution.x, iResolution.y);

    float time = iTime * 0.5;

    // Normalize mouse input (0.0 - 1.0) and remap to -1.0 ~ 1.0
    vec2 mouse = iMouse / iResolution;
    vec2 rippleCenter = 2.0 * mouse - 1.0;

    vec2 distortion = centeredUV;
    // Apply distortion for a wavy, smokey effect
    for (float i = 1.0; i < 8.0; i++) {
        distortion.x += 0.5 / i * cos(i * 2.0 * distortion.y + time + rippleCenter.x * 3.1415);
        distortion.y += 0.5 / i * cos(i * 2.0 * distortion.x + time + rippleCenter.y * 3.1415);
    }

    // Create a glowing wave pattern
    float wave = abs(sin(distortion.x + distortion.y + time));
    float glow = smoothstep(0.9, 0.2, wave);

    fragColor = vec4(u_color * glow, 1.0);
}

void main() {
    mainImage(gl_FragColor, gl_FragCoord.xy);
}
`;

/**
 * Valid blur sizes supported by Tailwind CSS.
 */
type BlurSize = 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

/**
 * Props for the SmokeyBackground component.
 */
interface SmokeyBackgroundProps {
  backdropBlurAmount?: string;
  color?: string;
  className?: string;
}

/**
 * A mapping from blur size names to Tailwind CSS classes.
 */
const blurClassMap: Record<BlurSize, string> = {
  none: 'backdrop-blur-none',
  sm: 'backdrop-blur-sm',
  md: 'backdrop-blur-md',
  lg: 'backdrop-blur-lg',
  xl: 'backdrop-blur-xl',
  '2xl': 'backdrop-blur-2xl',
  '3xl': 'backdrop-blur-3xl'
};

/**
 * An interactive WebGL shader background. Pointer position is tracked via a
 * ref so the WebGL context is created once (not torn down on every move).
 */
export function SmokeyBackground({
  backdropBlurAmount = 'sm',
  color = '#1E40AF',
  className = ''
}: SmokeyBackgroundProps): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const hoveringRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl');
    if (!gl) {
      console.error('WebGL not supported');
      return;
    }

    const hexToRgb = (hex: string): [number, number, number] => {
      const r = parseInt(hex.substring(1, 3), 16) / 255;
      const g = parseInt(hex.substring(3, 5), 16) / 255;
      const b = parseInt(hex.substring(5, 7), 16) / 255;
      return [r, g, b];
    };

    const compileShader = (type: number, source: string): WebGLShader | null => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error('Shader compilation error:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vertexShader = compileShader(gl.VERTEX_SHADER, vertexSmokeySource);
    const fragmentShader = compileShader(gl.FRAGMENT_SHADER, fragmentSmokeySource);
    if (!vertexShader || !fragmentShader) return;

    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program linking error:', gl.getProgramInfoLog(program));
      return;
    }

    gl.useProgram(program);

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    const iResolutionLocation = gl.getUniformLocation(program, 'iResolution');
    const iTimeLocation = gl.getUniformLocation(program, 'iTime');
    const iMouseLocation = gl.getUniformLocation(program, 'iMouse');
    const uColorLocation = gl.getUniformLocation(program, 'u_color');

    const startTime = Date.now();
    const [r, g, b] = hexToRgb(color);
    gl.uniform3f(uColorLocation, r, g, b);

    const render = () => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      canvas.width = width;
      canvas.height = height;
      gl.viewport(0, 0, width, height);

      const currentTime = (Date.now() - startTime) / 1000;

      gl.uniform2f(iResolutionLocation, width, height);
      gl.uniform1f(iTimeLocation, currentTime);
      gl.uniform2f(
        iMouseLocation,
        hoveringRef.current ? mouseRef.current.x : width / 2,
        hoveringRef.current ? height - mouseRef.current.y : height / 2
      );

      gl.drawArrays(gl.TRIANGLES, 0, 6);
      requestAnimationFrame(render);
    };

    const handleMouseMove = (event: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };
    const handleMouseEnter = () => {
      hoveringRef.current = true;
    };
    const handleMouseLeave = () => {
      hoveringRef.current = false;
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseenter', handleMouseEnter);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    render();

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseenter', handleMouseEnter);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [color]);

  const finalBlurClass = blurClassMap[backdropBlurAmount as BlurSize] || blurClassMap['sm'];

  return (
    <div className={`absolute inset-0 w-full h-full overflow-hidden ${className}`}>
      <canvas ref={canvasRef} className="w-full h-full" />
      <div className={`absolute inset-0 ${finalBlurClass}`}></div>
    </div>
  );
}

interface FloatingFieldProps {
  id: string;
  label: string;
  icon: ReactElement;
  type?: string;
  value: string;
  autoComplete?: string;
  onChange: (value: string) => void;
}

function FloatingField({
  id,
  label,
  icon,
  type = 'text',
  value,
  autoComplete,
  onChange
}: FloatingFieldProps): ReactElement {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const hasValue = value.length > 0;

  // Browser autofill/password managers write the input directly without
  // firing React's onChange, so the label would stay over the filled text.
  // Sync state with the real DOM value (live + shortly after mount).
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const sync = (): void => {
      if (input.value !== valueRef.current) onChange(input.value);
    };
    input.addEventListener('input', sync);
    input.addEventListener('change', sync);
    const t1 = window.setTimeout(sync, 400);
    const t2 = window.setTimeout(sync, 1500);
    return () => {
      input.removeEventListener('input', sync);
      input.removeEventListener('change', sync);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  return (
    <div className="relative">
      <input
        type={type}
        id={id}
        ref={inputRef}
        className="block w-full py-2.5 px-3 text-sm text-white bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-500"
        placeholder=""
        value={value}
        autoComplete={autoComplete}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={e => onChange(e.target.value)}
      />
      <label
        htmlFor={id}
        className={`pointer-events-none absolute left-3 top-3 flex items-center gap-1.5 text-sm transition-opacity duration-200 ${
          hasValue ? 'opacity-0' : 'opacity-100'
        } ${focused ? 'text-blue-400' : 'text-gray-300'}`}
      >
        <span className="inline-flex items-center">{icon}</span>
        {label}
      </label>
    </div>
  );
}

interface LoginFormProps {
  mode: AuthMode;
  bootOwner: boolean;
  invite: string;
  resetToken: string;
  onModeChange: (mode: AuthMode) => void;
}

/**
 * Glassmorphism auth form wired to the Work Track API. Supports login,
 * invite/boot-owner registration, a self-service "forgot password" flow, and
 * a one-time token password reset — mirroring src/auth.ts validation.
 */
export function LoginForm({ mode, bootOwner, invite, resetToken, onModeChange }: LoginFormProps): ReactElement {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmValue, setConfirmValue] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [inviteCode, setInviteCode] = useState(invite);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setInviteCode(invite);
  }, [invite]);

  const isRegister = mode === 'register';

  const submit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');

    const uname = username.trim();
    if (!uname) {
      setError('Username is required');
      return;
    }
    if (!/^[a-zA-Z0-9_.-]{3,32}$/.test(uname)) {
      setError('Username must be 3–32 chars (letters, numbers, . _ -)');
      return;
    }
    if (!password) {
      setError('Password is required');
      return;
    }
    if (isRegister && password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (isRegister) {
      const em = email.trim();
      if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
        setError('Invalid email address');
        return;
      }
      if (!bootOwner && !inviteCode.trim()) {
        setError('Invite code is required');
        return;
      }
    }

    setSubmitting(true);
    try {
      let data: { token: string; user: User };
      if (isRegister) {
        data = await api<{ token: string; user: User }>('/api/auth/register', 'POST', {
          username: uname,
          password,
          name: name.trim(),
          email: email.trim(),
          invite: inviteCode.trim()
        });
      } else {
        data = await api<{ token: string; user: User }>('/api/auth/login', 'POST', { username: uname, password });
      }
      state.token = data.token;
      state.user = data.user;
      localStorage.setItem('wt_token', data.token);
      onModeChange('login');
      showApp();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleMode = (): void => {
    setError('');
    onModeChange(isRegister ? 'login' : 'register');
  };

  const submitForgot = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    const em = email.trim();
    if (!em) {
      setError('Enter your account email');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setError('Invalid email address');
      return;
    }
    setSubmitting(true);
    try {
      await api<{ ok: boolean }>('/api/auth/forgot', 'POST', { email: em });
      setSent(true);
      setSubmitting(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
      setSubmitting(false);
    }
  };

  const submitReset = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    if (!password) {
      setError('Enter a new password');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    const confirm = e.currentTarget.elements.namedItem('confirm') as HTMLInputElement | null;
    if (confirm && confirm.value !== password) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      const data = await api<{ token: string; user: User }>('/api/auth/reset', 'POST', {
        token: resetToken,
        password
      });
      const u = new URL(window.location.href);
      u.searchParams.delete('reset');
      history.replaceState({}, '', u.pathname + u.search);
      state.token = data.token;
      state.user = data.user;
      localStorage.setItem('wt_token', data.token);
      onModeChange('login');
      showApp();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
      setSubmitting(false);
    }
  };

  const submitLabel = isRegister ? (bootOwner ? 'Create account' : 'Sign up') : 'Sign in';
  const title = isRegister ? (bootOwner ? 'Set up your workspace' : 'Join the team') : 'Welcome';
  const subtitle = isRegister
    ? bootOwner
      ? 'First account — becomes the workspace Owner.'
      : 'Registering requires an invite code from your organization.'
    : 'Sign in to continue';

  if (resetToken) {
    return (
      <div className="w-full max-w-sm p-5 space-y-4 bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 shadow-2xl">
        <div className="text-center">
          <h2 className="text-xl font-bold text-white">Set a new password</h2>
          <p className="mt-0.5 text-sm text-gray-300">Enter a new password for your account.</p>
        </div>
        <form className="space-y-5" onSubmit={submitReset}>
          <FloatingField
            id="authPassword"
            label="New password"
            icon={<Lock size={16} />}
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
          />
          <FloatingField
            id="authConfirm"
            label="Confirm password"
            icon={<Lock size={16} />}
            type="password"
            value={confirmValue}
            onChange={setConfirmValue}
            autoComplete="new-password"
          />
          <button
            type="submit"
            disabled={submitting}
            className="group w-full flex items-center justify-center py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg text-white font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500 transition-all duration-300"
          >
            {submitting ? 'Please wait…' : 'Reset password'}
            <ArrowRight className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
          </button>
        </form>
        {error && <p className="text-sm text-red-300 text-center" role="alert">{error}</p>}
      </div>
    );
  }

  if (forgot) {
    return (
      <div className="w-full max-w-sm p-5 space-y-4 bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 shadow-2xl">
        <div className="text-center">
          <h2 className="text-xl font-bold text-white">Forgot password</h2>
          <p className="mt-0.5 text-sm text-gray-300">
            {sent ? 'If an account exists for that email, a reset link has been sent.' : 'Enter your account email and we’ll send you a reset link.'}
          </p>
        </div>
        {!sent ? (
          <form className="space-y-5" onSubmit={submitForgot}>
            <FloatingField id="authForgotEmail" label="Email Address" icon={<Mail size={16} />} type="email" value={email} onChange={setEmail} autoComplete="email" />
            <button
              type="submit"
              disabled={submitting}
              className="group w-full flex items-center justify-center py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg text-white font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500 transition-all duration-300"
            >
              {submitting ? 'Please wait…' : 'Send reset link'}
              <ArrowRight className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        ) : null}
        {error && <p className="text-sm text-red-300 text-center" role="alert">{error}</p>}
        <button
          type="button"
          onClick={() => {
            setForgot(false);
            setSent(false);
            setError('');
          }}
          className="w-full text-center text-xs text-gray-300 hover:text-white transition"
        >
          ← Back to sign in
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm p-5 space-y-4 bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 shadow-2xl">
      <div className="text-center">
        <h2 className="text-xl font-bold text-white">{title}</h2>
        <p className="mt-0.5 text-sm text-gray-300">{subtitle}</p>
      </div>
      <form className="space-y-5" onSubmit={submit}>
        {isRegister && (
          <FloatingField id="authName" label="Name" icon={<UserIcon size={16} />} value={name} onChange={setName} autoComplete="name" />
        )}
        {isRegister && (
          <FloatingField id="authEmail" label="Email Address" icon={<Mail size={16} />} type="email" value={email} onChange={setEmail} autoComplete="email" />
        )}
        {isRegister && !bootOwner && (
          <FloatingField
            id="authInvite"
            label="Invite code"
            icon={<KeyRound size={16} />}
            value={inviteCode}
            onChange={setInviteCode}
            autoComplete="off"
          />
        )}
        <FloatingField id="authUsername" label="Username" icon={<UserIcon size={16} />} value={username} onChange={setUsername} autoComplete="username" />
        <FloatingField
          id="authPassword"
          label="Password"
          icon={<Lock size={16} />}
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete={isRegister ? 'new-password' : 'current-password'}
        />

        <div className="flex items-center justify-between">
          <a
            href="#"
            onClick={e => {
              e.preventDefault();
              setError('');
              setForgot(true);
            }}
            className="text-xs text-gray-300 hover:text-white transition"
          >
            Forgot Password?
          </a>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="group w-full flex items-center justify-center py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg text-white font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500 transition-all duration-300"
        >
          {submitting ? 'Please wait…' : submitLabel}
          <ArrowRight className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
        </button>

        {error && <p className="text-sm text-red-300 text-center" role="alert">{error}</p>}
      </form>
      <p className="text-center text-xs text-gray-400">
        {isRegister ? (
          <>
            Have an account?{' '}
            <a href="#" onClick={e => { e.preventDefault(); toggleMode(); }} className="font-semibold text-blue-400 hover:text-blue-300 transition">
              Sign in
            </a>
          </>
        ) : (
          <>
            Need an account?{' '}
            <a href="#" onClick={e => { e.preventDefault(); toggleMode(); }} className="font-semibold text-blue-400 hover:text-blue-300 transition">
              Sign up
            </a>
          </>
        )}
      </p>
    </div>
  );
}